import axios from 'axios';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { toastOnError } from '../../utils/Utils';


const initialState = {
    rates: {},
    seasons: {},
    brackets: {},
    partnerRates: {},
    defaultTransferRates: { seasons: [], rows: [] },
    partnerTransferRates: { seasons: [], rows: [] },
    defaultChauffeurRates: { seasons: [], blocks: [], rows: [] },
    partnerChauffeurRates: { seasons: [], blocks: [], rows: [] },
    transferSeasons: [],
    chauffeurSeasons: [],
    chauffeurBlocks: [],
    isLoading: false
}


export const getRates = createAsyncThunk(
    'rates',
    async (user, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.get('admin/rates/');
            dispatch(setRates(response.data));
            return response.data
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response.data);
        }
    }
)

export const getPartnerRates = createAsyncThunk(
    'partnerRates',
    async (user, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.get('partner/rates/');
            dispatch(setPartnerRates(response.data));
            return response.data
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response.data);
        }
    }
)

export const createPartnerRate = createAsyncThunk(
    'createPartnerRate',
    async (rateData, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.post('partner/rates/', rateData);
            dispatch(getPartnerRates());
            toast.success('Rate created');
            return response.data;
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response.data);
        }
    }
)

export const updatePartnerRate = createAsyncThunk(
    'updatePartnerRate',
    async (rateData, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.patch(`partner/rates/${rateData.id}/`, rateData);
            dispatch(getPartnerRates());
            toast.success('Rate updated');
            return response.data;
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response.data);
        }
    }
)

export const deletePartnerRate = createAsyncThunk(
    'deletePartnerRate',
    async (rateId, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.delete(`partner/rates/${rateId}/`);
            dispatch(getPartnerRates());
            toast.success('Rate deleted');
            return response.data;
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response.data);
        }
    }
)

export const updateRates = createAsyncThunk(
    'rates/update',
    async (updateData, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.post('admin/rates/', updateData);
            dispatch(getRates());
            toast.success('Rates updated');
            return response.data
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response.data);
        }
    }
)

export const getSeasons = createAsyncThunk(
    'seasons',
    async (user, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.get('etc/seasons');
            dispatch(setSeasons(response.data));
            return response.data
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response.data);
        }
    }
)

export const getBrackets = createAsyncThunk(
    'brackets',
    async (user, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.get('etc/brackets');
            dispatch(setBrackets(response.data));
            return response.data
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response.data);
        }
    }
)

// --- Transfer Rates Actions ---
export const getDefaultTransferRates = createAsyncThunk(
    'defaultTransferRates',
    async (_, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.get('admin/transfer-rates/');
            dispatch(setDefaultTransferRates(response.data));
            return response.data;
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
)

export const updateDefaultTransferRates = createAsyncThunk(
    'defaultTransferRates/update',
    async (updateData, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.post('admin/transfer-rates/', updateData);
            dispatch(getDefaultTransferRates());
            toast.success('Transfer rates updated');
            return response.data;
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
)

export const getPartnerTransferRates = createAsyncThunk(
    'partnerTransferRates',
    async (_, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.get('partner/transfer-rates/');
            dispatch(setPartnerTransferRates(response.data));
            return response.data;
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
)

export const updatePartnerTransferRates = createAsyncThunk(
    'partnerTransferRates/update',
    async (updateData, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.post('partner/transfer-rates/', updateData);
            dispatch(getPartnerTransferRates());
            toast.success('Partner transfer rates updated');
            return response.data;
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
)

export const getTransferSeasons = createAsyncThunk(
    'transferSeasons',
    async (_, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.get('etc/transfer-seasons/');
            dispatch(setTransferSeasons(response.data));
            return response.data;
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
)

// --- Chauffeur Rates Actions ---
export const getDefaultChauffeurRates = createAsyncThunk(
    'defaultChauffeurRates',
    async (_, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.get('admin/chauffeur-rates/');
            dispatch(setDefaultChauffeurRates(response.data));
            return response.data;
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
)

export const updateDefaultChauffeurRates = createAsyncThunk(
    'defaultChauffeurRates/update',
    async (updateData, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.post('admin/chauffeur-rates/', updateData);
            dispatch(getDefaultChauffeurRates());
            toast.success('Chauffeur rates updated');
            return response.data;
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
)

export const getPartnerChauffeurRates = createAsyncThunk(
    'partnerChauffeurRates',
    async (_, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.get('partner/chauffeur-rates/');
            dispatch(setPartnerChauffeurRates(response.data));
            return response.data;
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
)

export const updatePartnerChauffeurRates = createAsyncThunk(
    'partnerChauffeurRates/update',
    async (updateData, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.post('partner/chauffeur-rates/', updateData);
            dispatch(getPartnerChauffeurRates());
            toast.success('Partner chauffeur rates updated');
            return response.data;
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
)

export const getChauffeurSeasons = createAsyncThunk(
    'chauffeurSeasons',
    async (_, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.get('etc/chauffeur-seasons/');
            dispatch(setChauffeurSeasons(response.data));
            return response.data;
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
)

export const getChauffeurBlocks = createAsyncThunk(
    'chauffeurBlocks',
    async (_, {dispatch, rejectWithValue}) => {
        try {
            const response = await axios.get('etc/chauffeur-blocks/');
            dispatch(setChauffeurBlocks(response.data));
            return response.data;
        } catch (error) {
            toastOnError(error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
)


const ratesSlice = createSlice({
    name: 'rates',
    initialState,
    reducers: {
        setRates (state, action) {
            state.rates = action.payload;
        },
        setPartnerRates (state, action) {
            state.partnerRates = action.payload;
        },
        setSeasons (state, action) {
            state.seasons = action.payload;
        },
        setBrackets (state, action) {
            state.brackets = action.payload;
        },
        setDefaultTransferRates (state, action) {
            state.defaultTransferRates = action.payload;
        },
        setPartnerTransferRates (state, action) {
            state.partnerTransferRates = action.payload;
        },
        setDefaultChauffeurRates (state, action) {
            state.defaultChauffeurRates = action.payload;
        },
        setPartnerChauffeurRates (state, action) {
            state.partnerChauffeurRates = action.payload;
        },
        setTransferSeasons (state, action) {
            state.transferSeasons = action.payload;
        },
        setChauffeurSeasons (state, action) {
            state.chauffeurSeasons = action.payload;
        },
        setChauffeurBlocks (state, action) {
            state.chauffeurBlocks = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getRates.pending, (state) => { state.isLoading = true; })
            .addCase(getRates.fulfilled, (state) => { state.isLoading = false; })
            .addCase(getRates.rejected, (state) => { state.isLoading = false; })
            .addCase(getSeasons.pending, (state) => { state.isLoading = true; })
            .addCase(getSeasons.fulfilled, (state) => { state.isLoading = false; })
            .addCase(getSeasons.rejected, (state) => { state.isLoading = false; })
            .addCase(getPartnerRates.pending, (state) => { state.isLoading = true; })
            .addCase(getPartnerRates.fulfilled, (state) => { state.isLoading = false; })
            .addCase(getPartnerRates.rejected, (state) => { state.isLoading = false; })
            .addCase(getDefaultTransferRates.pending, (state) => { state.isLoading = true; })
            .addCase(getDefaultTransferRates.fulfilled, (state) => { state.isLoading = false; })
            .addCase(getDefaultTransferRates.rejected, (state) => { state.isLoading = false; })
            .addCase(getPartnerTransferRates.pending, (state) => { state.isLoading = true; })
            .addCase(getPartnerTransferRates.fulfilled, (state) => { state.isLoading = false; })
            .addCase(getPartnerTransferRates.rejected, (state) => { state.isLoading = false; })
            .addCase(getDefaultChauffeurRates.pending, (state) => { state.isLoading = true; })
            .addCase(getDefaultChauffeurRates.fulfilled, (state) => { state.isLoading = false; })
            .addCase(getDefaultChauffeurRates.rejected, (state) => { state.isLoading = false; })
            .addCase(getPartnerChauffeurRates.pending, (state) => { state.isLoading = true; })
            .addCase(getPartnerChauffeurRates.fulfilled, (state) => { state.isLoading = false; })
            .addCase(getPartnerChauffeurRates.rejected, (state) => { state.isLoading = false; })
    }
})

export const {
    setRates,
    setPartnerRates,
    setSeasons,
    setBrackets,
    setDefaultTransferRates,
    setPartnerTransferRates,
    setDefaultChauffeurRates,
    setPartnerChauffeurRates,
    setTransferSeasons,
    setChauffeurSeasons,
    setChauffeurBlocks
} = ratesSlice.actions;

export default ratesSlice.reducer;
